import {Box, Center, HStack, Icon, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {findIndex, isNil} from 'ramda';

import {CoreExchangeProcessMilestone, CoreExchangeProcessStep} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface ProcessMilestonesProps extends RequiredTestIdProps {
  milestones: CoreExchangeProcessMilestone[] | Nullish;
}

type MilestoneState = 'done' | 'denied' | 'current' | 'upcoming';

type StepLabels = {
  title: string;
  waiting: string;
  done: string;
};

const STEP_CIRCLE_SIZE = 8;

const STEP_LABELS: Record<CoreExchangeProcessStep, StepLabels> = {
  DELIVERY: {
    title: 'entity.warehouse.labels.coreExchangeStepDelivery',
    waiting: 'entity.warehouse.labels.coreExchangeStepDeliveryWaiting',
    done: 'entity.warehouse.labels.coreExchangeStepDeliveryDone',
  },
  INSPECTION: {
    title: 'module.inspection.title',
    waiting: 'entity.warehouse.labels.coreExchangeStepInspectionWaiting',
    done: 'entity.warehouse.labels.coreExchangeStepInspectionDone',
  },
  DEPOSIT: {
    title: 'entity.warehouse.labels.warehouse',
    waiting: 'entity.warehouse.labels.coreExchangeStepDepositWaiting',
    done: 'entity.warehouse.labels.coreExchangeStepDepositDone',
  },
  REQUEST: {
    title: 'entity.warehouse.labels.coreExchangeStepReturnAuthorisationRequest',
    waiting: 'entity.warehouse.labels.coreExchangeStepRequestWaiting',
    done: 'entity.warehouse.labels.coreExchangeStepRequestDone',
  },
  AUTHORISATION: {
    title: 'entity.warehouse.labels.coreExchangeReturnAuthorisation',
    waiting: 'entity.warehouse.labels.coreExchangeStepAuthorisationWaiting',
    done: 'entity.warehouse.labels.coreExchangeStepAuthorisationDone',
  },
  RETURN: {
    title: 'entity.warehouse.labels.coreExchangeReturnToSupplier',
    waiting: 'entity.warehouse.labels.coreExchangeStepReturnWaiting',
    done: 'entity.warehouse.labels.coreExchangeStepReturnDone',
  },
};

const getCircleColor = (state: MilestoneState) =>
  match(state)
    .returnType<ThemeColorPath>()
    .with('done', () => 'severity.success')
    .with('denied', () => 'severity.danger')
    .with('current', () => 'palettes.blue.60.100')
    .with('upcoming', () => 'palettes.neutral.40.100')
    .exhaustive();

export function ProcessMilestones(props: ProcessMilestonesProps) {
  const formatDateTime = useDateTimeFormatter();

  if (isNil(props.milestones)) {
    return null;
  }

  const currentIndex = findIndex((milestone) => !milestone.isDone, props.milestones);

  const getMilestoneState = (milestone: CoreExchangeProcessMilestone, index: number) =>
    match({status: milestone.status, isDone: milestone.isDone, isCurrent: index === currentIndex})
      .returnType<MilestoneState>()
      .with({status: Pattern.string.endsWith('_DENIED')}, () => 'denied')
      .with({status: Pattern.string.endsWith('_NOT_DELIVERED')}, () => 'denied')
      .with({isDone: true}, () => 'done')
      .with({isCurrent: true}, () => 'current')
      .otherwise(() => 'upcoming');

  const getCaption = (milestone: CoreExchangeProcessMilestone, state: MilestoneState) => {
    const date = isNil(milestone.date)
      ? ''
      : formatDateTime('dateTimeShort', parseDate(milestone.date));

    return match({state, status: milestone.status})
      .with({status: Pattern.string.endsWith('_NOT_DELIVERED')}, () =>
        i18n.t('entity.businessCase.financingEnding.operation.result.notDelivered')
      )
      .with({state: 'denied'}, () =>
        i18n.t('entity.warehouse.labels.coreExchangeStepDenied', {date})
      )
      .with({state: 'done'}, () => i18n.t(STEP_LABELS[milestone.step].done, {date}))
      .otherwise(() => i18n.t(STEP_LABELS[milestone.step].waiting));
  };

  return (
    <HStack spacing={2} data-testid={suffixTestId('wrapper', props)}>
      {props.milestones.map((milestone, index) => {
        const state = getMilestoneState(milestone, index);
        const circleColor = getCircleColor(state);
        const isFinished = state === 'done' || state === 'denied';

        return (
          <Box key={milestone.step} flex={1}>
            <VStack spacing={1} align="center">
              <Box
                width={STEP_CIRCLE_SIZE}
                height={STEP_CIRCLE_SIZE}
                borderRadius="circular"
                border="1px solid"
                borderColor={circleColor}
                backgroundColor={isFinished ? circleColor : undefined}
              >
                <Center height="100%">
                  <Show when={state === 'done'}>
                    <Icon value="navigation/check" size={4} color="general.white" />
                  </Show>
                  <Show when={state === 'denied'}>
                    <Icon value="navigation/close" size={4} color="general.white" />
                  </Show>
                  <Show when={!isFinished}>
                    <Text size="small" color={state === 'current' ? 'link' : 'tertiary'}>
                      {(index + 1).toString()}
                    </Text>
                  </Show>
                </Center>
              </Box>
              <Text
                size="small"
                alternative
                align="center"
                data-testid={suffixTestId(`step.${milestone.step}.title`, props)}
              >
                {i18n.t(STEP_LABELS[milestone.step].title)}
              </Text>
              <Text
                size="xSmall"
                color="tertiary"
                align="center"
                data-testid={suffixTestId(`step.${milestone.step}.caption`, props)}
              >
                {getCaption(milestone, state)}
              </Text>
            </VStack>
          </Box>
        );
      })}
    </HStack>
  );
}
